import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { useMessages } from '../Messages/MessagesContext';
import './MarkAsSoldDialog.css';

const EMPTY_FORM = {
    saleChannel: 'platform',
    buyerUsername: '',
    elsewhereLocation: '',
    elsewhereBuyerName: '',
};

const MarkAsSoldDialog = ({
    open,
    listingTitle,
    busy = false,
    onConfirm,
    onCancel,
}) => {
    const { conversations, fetchConversations } = useMessages();
    const [form, setForm] = useState(EMPTY_FORM);
    const [validationError, setValidationError] = useState('');
    const [buyerSuggestionsOpen, setBuyerSuggestionsOpen] = useState(false);
    const buyerBlurTimeoutRef = useRef(null);
    const buyerListId = 'msv-sold-buyer-suggestions';

    const chatUsernames = useMemo(() => {
        const seen = new Set();
        const names = [];
        for (const conversation of conversations || []) {
            const name = (conversation.otherUsername || '').trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            names.push(name);
        }
        return names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [conversations]);

    const filteredChatUsernames = useMemo(() => {
        const query = form.buyerUsername.trim().toLowerCase();
        if (!query) return chatUsernames;
        return chatUsernames.filter((name) => name.toLowerCase().includes(query));
    }, [chatUsernames, form.buyerUsername]);

    useEffect(() => {
        if (!open) return;
        setForm(EMPTY_FORM);
        setValidationError('');
        setBuyerSuggestionsOpen(false);
        fetchConversations?.();
    }, [open, fetchConversations]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !busy) onCancel?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, busy, onCancel]);

    useEffect(() => () => {
        if (buyerBlurTimeoutRef.current) {
            clearTimeout(buyerBlurTimeoutRef.current);
        }
    }, []);

    if (!open) return null;

    const showBuyerSuggestions =
        buyerSuggestionsOpen
        && !busy
        && filteredChatUsernames.length > 0;

    const handleBuyerFocus = () => {
        if (buyerBlurTimeoutRef.current) {
            clearTimeout(buyerBlurTimeoutRef.current);
            buyerBlurTimeoutRef.current = null;
        }
        if (chatUsernames.length > 0) {
            setBuyerSuggestionsOpen(true);
        }
    };

    const handleBuyerBlur = () => {
        buyerBlurTimeoutRef.current = setTimeout(() => {
            setBuyerSuggestionsOpen(false);
        }, 150);
    };

    const selectBuyerUsername = (username) => {
        setForm((prev) => ({ ...prev, buyerUsername: username }));
        setValidationError('');
        setBuyerSuggestionsOpen(false);
    };

    const handleConfirm = () => {
        if (form.saleChannel === 'platform') {
            const soldToUsername = form.buyerUsername.trim();
            if (!soldToUsername) {
                setValidationError('Enter the buyer\'s My Speed Vault username.');
                return;
            }
            onConfirm({
                soldOnPlatform: true,
                soldToUsername,
            });
            return;
        }

        const soldElsewhereDetail = form.elsewhereLocation.trim();
        if (!soldElsewhereDetail) {
            setValidationError('Tell us where this item was sold.');
            return;
        }

        onConfirm({
            soldOnPlatform: false,
            soldElsewhereDetail,
            soldToName: form.elsewhereBuyerName.trim() || null,
        });
    };

    return (
        <div className="msv-sold-overlay" role="presentation" onClick={() => !busy && onCancel?.()}>
            <div
                className="msv-sold-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="msv-sold-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="msv-sold-icon" aria-hidden="true">
                    <FiCheckCircle />
                </div>
                <h3 id="msv-sold-title">Mark as sold?</h3>
                <p className="msv-sold-intro">
                    {listingTitle
                        ? `Confirm the sale for "${listingTitle}". This helps us verify platform sales if needed.`
                        : 'Confirm this sale. This helps us verify platform sales if needed.'}
                </p>

                <fieldset className="msv-sold-fieldset">
                    <legend>Where was it sold?<span className="asterisk">*</span></legend>
                    <label className="msv-sold-choice">
                        <input
                            type="radio"
                            name="saleChannel"
                            value="platform"
                            checked={form.saleChannel === 'platform'}
                            disabled={busy}
                            onChange={() => {
                                setForm((prev) => ({ ...prev, saleChannel: 'platform' }));
                                setValidationError('');
                            }}
                        />
                        <span>Sold on My Speed Vault</span>
                    </label>
                    <label className="msv-sold-choice">
                        <input
                            type="radio"
                            name="saleChannel"
                            value="elsewhere"
                            checked={form.saleChannel === 'elsewhere'}
                            disabled={busy}
                            onChange={() => {
                                setForm((prev) => ({ ...prev, saleChannel: 'elsewhere' }));
                                setValidationError('');
                            }}
                        />
                        <span>Sold somewhere else</span>
                    </label>
                </fieldset>

                {form.saleChannel === 'platform' ? (
                    <div className="msv-sold-fields">
                        <label htmlFor="msv-sold-buyer-username">Buyer username<span className="asterisk">*</span></label>
                        <div className="msv-sold-combobox">
                            <input
                                id="msv-sold-buyer-username"
                                type="text"
                                role="combobox"
                                value={form.buyerUsername}
                                disabled={busy}
                                placeholder="MSV username of the buyer"
                                autoComplete="off"
                                aria-autocomplete="list"
                                aria-expanded={showBuyerSuggestions}
                                aria-controls={showBuyerSuggestions ? buyerListId : undefined}
                                onFocus={handleBuyerFocus}
                                onBlur={handleBuyerBlur}
                                onChange={(event) => {
                                    setForm((prev) => ({ ...prev, buyerUsername: event.target.value }));
                                    setValidationError('');
                                    setBuyerSuggestionsOpen(chatUsernames.length > 0);
                                }}
                            />
                            {showBuyerSuggestions ? (
                                <ul
                                    id={buyerListId}
                                    className="msv-sold-suggestions"
                                    role="listbox"
                                    aria-label="People you have messaged"
                                >
                                    {filteredChatUsernames.map((username) => (
                                        <li key={username.toLowerCase()} role="presentation">
                                            <button
                                                type="button"
                                                role="option"
                                                aria-selected={form.buyerUsername === username}
                                                className="msv-sold-suggestion"
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => selectBuyerUsername(username)}
                                            >
                                                {username}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                        <p className="msv-sold-hint">
                            Pick someone you&apos;ve messaged or type their exact MSV username. The item will leave your collection and move into theirs.
                        </p>
                    </div>
                ) : (
                    <div className="msv-sold-fields">
                        <label htmlFor="msv-sold-elsewhere-location">Where was it sold?<span className="asterisk">*</span></label>
                        <input
                            id="msv-sold-elsewhere-location"
                            type="text"
                            value={form.elsewhereLocation}
                            disabled={busy}
                            placeholder="eBay, Facebook Marketplace, in person, etc."
                            onChange={(event) => {
                                setForm((prev) => ({ ...prev, elsewhereLocation: event.target.value }));
                                setValidationError('');
                            }}
                        />
                        <label htmlFor="msv-sold-elsewhere-buyer">Sold to (optional)</label>
                        <input
                            id="msv-sold-elsewhere-buyer"
                            type="text"
                            value={form.elsewhereBuyerName}
                            disabled={busy}
                            placeholder="Buyer name or handle"
                            onChange={(event) => {
                                setForm((prev) => ({ ...prev, elsewhereBuyerName: event.target.value }));
                                setValidationError('');
                            }}
                        />
                        <p className="msv-sold-hint">
                            This removes the item from your MSV collection since it was sold elsewhere.
                        </p>
                    </div>
                )}

                {validationError ? (
                    <p className="msv-sold-error" role="alert">{validationError}</p>
                ) : null}

                <div className="msv-sold-actions">
                    <button
                        type="button"
                        className="msv-sold-cancel"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="msv-sold-confirm"
                        onClick={handleConfirm}
                        disabled={busy}
                    >
                        {busy ? 'Saving…' : 'Mark as sold'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MarkAsSoldDialog;
